import { useState, useEffect, useCallback, useRef, use } from 'react';
import PropTypes from 'prop-types';
import Metrics from '@spotahome/soyuz-tracking';

import YouTubeIframeLoader from './YouTubeIframeLoader';
import YoutubePlaceholder from './YoutubePlaceholder';

const YoutubeVideo = ({
  video,
  title,
  thumbnail = {},
  autoplay = false,
  watermark = null,
  listingId
}) => {
  const [requestedLoad, setRequestedLoad] = useState(autoplay);
  const playerRef = useRef(null);
  const currentVideoId = useRef();

  const loadVideo = useCallback(async videoId => {
    try {
      YouTubeIframeLoader.load(YT => {
        playerRef.current = new YT.Player(`yt-${videoId}`, {
          videoId,
          height: '100%',
          width: '100%',
          playerVars: {
            rel: 0
          },
          events: {
            onReady: params => {
              playerRef.current = params.target;
              params.target.playVideo();
            },
            onStateChange: () => {
              if (playerRef.current.setOption) {
                playerRef.current.setOption('captions', 'track', {
                  languageCode: 'en'
                });
              }
            },
            onError: event => {
              console.error(`Error: ${event.data}`);
            }
          }
        });
      });
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    const prevVideoId = currentVideoId.current;

    if (prevVideoId !== video.videoId) {
      if (!autoplay) {
        setRequestedLoad(false);
      } else if (requestedLoad) {
        loadVideo(video.videoId);
      }
    }

    currentVideoId.current = video.videoId;
  }, [requestedLoad, video.videoId, loadVideo]);

  const requestVideoLoad = () => {
    setRequestedLoad(true);
    loadVideo(video.videoId);
    Metrics.actions.sendActionEvent(Metrics.actions.WATCH_VERIFICATION_VIDEO, {
      listingId
    });
  };

  return (
    <div className="yt-video">
      {!requestedLoad && (
        <YoutubePlaceholder
          name={title}
          id={video.videoId}
          url={video.embedUrl}
          handleClick={requestVideoLoad}
          thumbnail={thumbnail}
        />
      )}
      <div className="yt-video__container" key={`video-${video.embedUrl}`}>
        <div id={`yt-${video.videoId}`} />
        {watermark}
      </div>
    </div>
  );
};

YoutubeVideo.propTypes = {
  video: PropTypes.shape({
    embedUrl: PropTypes.string,
    videoId: PropTypes.string,
    description: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    uploadDate: PropTypes.string
  }).isRequired,
  title: PropTypes.string.isRequired,
  thumbnail: YoutubePlaceholder.propTypes.thumbnail,
  autoplay: PropTypes.bool,
  watermark: PropTypes.node,
  listingId: PropTypes.string.isRequired
};

export default YoutubeVideo;
